import type { Sector } from '@/Network/Types/Responses/SectorResponses'
import type {
  SpecialtyOption,
  SpecialtyOptionsResponse
} from '@/Network/Types/Responses/SpecialtyResponses'
import type {
  StationSectorRef,
  StationSpecialtyRef
} from '@/Network/Types/Responses/StationResponses'
import type {
  BulkSectorSpecialtyRequest,
  StationSpecialtyInput
} from '@/Network/Types/Requests/StationRequests'

export type SpecialtySelection =
  | { kind: 'official'; id: number; name: string }
  | { kind: 'company'; id: number; name: string }
  | { kind: 'new'; name: string }

/** Mesma regra do BE (spec §3.1/H3): minúsculas, sem acento, espaços colapsados. */
export function normalizeCatalogName(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
}

export function matchesCatalogSearch(name: string, query: string): boolean {
  const q = normalizeCatalogName(query)
  return !q || normalizeCatalogName(name).includes(q)
}

export function findSectorByName(name: string, sectors: Sector[]): Sector | null {
  const target = normalizeCatalogName(name)
  if (!target) return null
  return sectors.find((sector) => normalizeCatalogName(sector.name) === target) ?? null
}

export function pendingSectorSuggestions(suggestions: string[], sectors: Sector[]): string[] {
  return suggestions.filter((name) => !findSectorByName(name, sectors))
}

export function findSpecialtyMatch(
  name: string,
  options: SpecialtyOptionsResponse | null
): { kind: 'official' | 'company'; option: SpecialtyOption } | null {
  const target = normalizeCatalogName(name)
  if (!target || !options) return null
  const official = options.official.find((o) => normalizeCatalogName(o.name) === target)
  if (official) return { kind: 'official', option: official }
  const company = options.company.find((o) => normalizeCatalogName(o.name) === target)
  return company ? { kind: 'company', option: company } : null
}

export function toSpecialtyInput(
  selection: SpecialtySelection | null
): StationSpecialtyInput | null {
  if (!selection) return null
  if (selection.kind === 'official') return { officialId: selection.id }
  if (selection.kind === 'company') return { companyId: selection.id }
  return { newName: selection.name.trim() }
}

export function specialtyRefToSelection(
  ref?: StationSpecialtyRef | null
): SpecialtySelection | null {
  if (!ref) return null
  return { kind: ref.source === 'official' ? 'official' : 'company', id: ref.id, name: ref.name }
}

export function specialtySelectionKey(selection: SpecialtySelection | null): string | null {
  if (!selection) return null
  if (selection.kind === 'new') return `n:${normalizeCatalogName(selection.name)}`
  return `${selection.kind === 'official' ? 'o' : 'c'}:${selection.id}`
}

export const sectorRefKey = (ref?: StationSectorRef | null): string | null =>
  ref ? `s:${ref.id}` : null

export type SectorSpecialtyValue = {
  sector: StationSectorRef | null
  specialty: SpecialtySelection | null
}

/** Semântica de patch da spec §4.2: ausente = mantém; null = limpa. */
export type SectorSpecialtyPatch = {
  sectorId?: number | null
  specialty?: StationSpecialtyInput | null
}

export function buildSectorSpecialtyPatch(
  initial: SectorSpecialtyValue,
  current: SectorSpecialtyValue
): SectorSpecialtyPatch {
  const patch: SectorSpecialtyPatch = {}
  if ((initial.sector?.id ?? null) !== (current.sector?.id ?? null)) {
    patch.sectorId = current.sector?.id ?? null
  }
  if (specialtySelectionKey(initial.specialty) !== specialtySelectionKey(current.specialty)) {
    patch.specialty = toSpecialtyInput(current.specialty)
  }
  return patch
}

export const hasSectorSpecialtyChanges = (patch: SectorSpecialtyPatch): boolean =>
  'sectorId' in patch || 'specialty' in patch

export function patchToBulkRequest(
  ids: number[],
  patch: SectorSpecialtyPatch
): BulkSectorSpecialtyRequest {
  const sector: BulkSectorSpecialtyRequest['sector'] = !('sectorId' in patch)
    ? { mode: 'keep' }
    : patch.sectorId === null || patch.sectorId === undefined
      ? { mode: 'clear' }
      : { mode: 'set', id: patch.sectorId }
  const specialty: BulkSectorSpecialtyRequest['specialty'] = !('specialty' in patch)
    ? { mode: 'keep' }
    : patch.specialty
      ? { mode: 'set', value: patch.specialty }
      : { mode: 'clear' }
  return { ids, sector, specialty }
}
